import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  FormLabel,
  Grid,
  styled,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useRecoilState, useSetRecoilState } from 'recoil'
import * as yup from 'yup'
import useSnackbar from '../../data/use-snackbar'
import simService from '../../services/sim.service'
import { formDataState, loadingState } from '../helpers'
import Layout from '../layout/layout.component'
import { getErrorMessage } from '../../error-helper'
import CloseIcon from '@mui/icons-material/Close'

const PREFIX = 'Portin-Step2'

export const classes = {
  label: `${PREFIX}-label`,
  containerButton: `${PREFIX}-container-button`,
}

export const styles = ({ theme }) => ({
  [`& .${classes.label}`]: {
    fontSize: theme.spacing(1.5),
    color: '#505050',
    fontWeight: '600',
  },
  [`& .${classes.label} .MuiFormLabel-asterisk`]: {
    color: theme.palette.primary.main,
  },
  [`& .${classes.containerButton}`]: {
    marginTop: theme.spacing(10),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0, 3),
      marginTop: theme.spacing(16),
    },
  },
})

const StyledDiv = styled('div')(styles)
function Step3({ step, handleBack, handlePortin }) {
  const [formData, setFormData] = useRecoilState(formDataState)
  const { enqueueSnackbar } = useSnackbar()
  const [dialogOpen, setDialogOpen] = useState(false)
  const setLoading = useSetRecoilState(loadingState)

  const validationSchema = yup.object().shape({
    msisdn: yup
      .string()
      .required('This field cannot be empty.')
      .matches(
        /^[8|9][0-9]{7}$/,
        'Your mobile number should contain 8 numerical numbers.'
      ),
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      msisdn: formData.portinMsisdn || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      const request = {
        msisdn: data.msisdn,
        iccid: '',
        imsi: '',
        status: 'ALL',
      }
      const result = await simService.check(request)
      if (result.code === 2000) {
        return enqueueSnackbar(`${data.msisdn} is an M1 Prepaid Number`)
      }

      setFormData({
        ...formData,
        portinMsisdn: data.msisdn,
      })
      setDialogOpen(true)
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async () => {
    setDialogOpen(false)
    await handlePortin()
  }

  const handleOnClose = () => {
    setDialogOpen(false)
  }

  return (
    <Layout
      step={step}
      content={
        <StyledDiv>
          <Grid container columnSpacing={8}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">
                Now, key in your existing number that you wish to port-in to M1.
                <br />
                <br />
                You can only port-in non-M1 Prepaid numbers registered under
                your ID.
                <br />
                <br />
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <FormLabel className={classes.label} required>
                  MSISDN / Your existing mobile number
                </FormLabel>
                <Controller
                  control={control}
                  name="msisdn"
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      placeholder="9000 0000"
                      error={errors.msisdn ? true : false}
                      helperText={errors.msisdn?.message}
                    />
                  )}
                />
                <Grid
                  item
                  container
                  xs={12}
                  md={12}
                  columnSpacing={1}
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  className={classes.containerButton}
                >
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={handleBack}
                    >
                      Back
                    </Button>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={!isValid}
                      type="submit"
                    >
                      Confirm
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
          <Dialog
            open={dialogOpen}
            onClose={handleOnClose}
            maxWidth="xs"
            keepMounted
          >
            <DialogTitle
              sx={{ m: 0, p: 2 }}
              id="customized-dialog-title"
            ></DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleOnClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <Typography variant="subtitle2" textAlign={'center'}>
                You have requested to port-in
              </Typography>
              <Typography variant="h2" textAlign={'center'}>
                {formData.portinMsisdn} <br />
                <br />
              </Typography>
              <Typography variant="subtitle2" textAlign={'center'}>
                Please ensure that the port-in number is correct before
                proceeding. <br />
                <br />
                While we port-in your number to M1.
                <br />
                Please use <b>{formData.msisdn}</b> as your temporary number.
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                p: '0px 20px 20px 20px',
              }}
            >
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleConfirm}
              >
                {'Proceed'}
              </Button>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={handleOnClose}
              >
                Back to edit
              </Button>
            </DialogActions>
          </Dialog>
        </StyledDiv>
      }
    />
  )
}

Step3.propTypes = {
  step: PropTypes.object.isRequired,
  handleBack: PropTypes.func.isRequired,
  handlePortin: PropTypes.func.isRequired,
}

export default Step3
